/**
 * Wire types for the deterministic migrations ENGINE.
 *
 * Distinct from the playbook path (`services/migrations/normalize.ts`), which runs an
 * LLM against an org and returns prose plus files. The engine compiles one metadata
 * component into another under a parity contract and its entire product is a VERDICT:
 * what was carried, what was dropped and why, and what a human must still do by hand.
 *
 * THESE SHAPES ARE NO LONGER GUESSES. Everything below is the shape the backend
 * actually emits (`ncoder-playground` `app/migrations_api.py` + `migration_engine/
 * executor.plan_report`), read off the source and confirmed against a live 37 KB
 * report. Where this file previously accepted two spellings of a field "in case", it
 * now names the one the wire uses — a normalizer that reads both spellings of a field
 * is a normalizer nobody can tell is broken.
 *
 * Four rules from the backend shape everything below and are load-bearing in the UI:
 *
 *  1. `undeclared` is ALWAYS reported, including at zero. It is the count of effects
 *     that were dropped with no declared reason — silent loss, the one failure the
 *     engine exists to make impossible. It is not optional and must never be omitted
 *     from a render just because it is empty.
 *  2. Every effect lands in exactly one of SIX buckets. `carried` is deliberately NOT
 *     included in the report payload (838 KB of an 876 KB real report); it is reachable
 *     only through the paged `/effects?bucket=carried` endpoint.
 *  3. COUNTS ARE RETURNED, NEVER DERIVED. `/report` paginates, so `undeclared.length`
 *     is the length of a page and `undeclared_count` is the truth. The engine returns
 *     every count beside the list it counts precisely so no client sums a page.
 *  4. An approval is bound to a `compile_key`. `compile_key` is a hash over every input
 *     that can change a byte of the artifact, so an artifact that recompiles gets a new
 *     one and a recorded approval stops naming it. The write path refuses on the
 *     mismatch; this UI must never present an approval whose key it has not checked.
 */

/** Wire outcome for a task's check-only org validation. LOWERCASE on the wire. */
export type EngineValidationOutcome = "validated" | "validation_failed" | "unvalidated";

// ── the families ────────────────────────────────────────────────────────────────

/**
 * The migration families, spelled as the wire spells them.
 *
 * THREE, not two. `process_to_flow` is Phase 3 and it is not a variant of the WorkflowRule
 * lane: it shares that lane's TARGET half entirely (the same FlowIR, validator and
 * compiler) and none of its source half, because a Process Builder process's criteria are
 * an ordered SEQUENCE of decision nodes rather than one entry condition. A Flow compiled
 * from a process therefore has EMPTY entry filters by construction, which is why the
 * engine grades it against `process_contract` — grading it against the rule contract would
 * report every process as a total loss.
 *
 * These strings are the `family` field on a plan and the `family` a plan is created with;
 * `migrations_api.FAMILIES` asserts equality with `executor.FAMILIES` server-side so the
 * list cannot silently disagree with the engine's own table.
 */
export const FAMILY_PROFILE_TO_PERMISSION_SET = "profile_to_permission_set";
export const FAMILY_WORKFLOW_TO_FLOW = "workflow_to_flow";
export const FAMILY_PROCESS_TO_FLOW = "process_to_flow";

export type EngineFamily =
  | typeof FAMILY_PROFILE_TO_PERMISSION_SET
  | typeof FAMILY_WORKFLOW_TO_FLOW
  | typeof FAMILY_PROCESS_TO_FLOW;

/** Every family a plan may be created for, in the server's own order. */
export const ENGINE_FAMILIES: readonly EngineFamily[] = [
  FAMILY_PROFILE_TO_PERMISSION_SET,
  FAMILY_WORKFLOW_TO_FLOW,
  FAMILY_PROCESS_TO_FLOW,
] as const;

// ── plan scope ──────────────────────────────────────────────────────────────────

/**
 * What KIND of plan a row is. `component` is one named component; `org` is a portfolio
 * pass over the whole inventory.
 *
 * They differ in cost by three orders of magnitude and in which routes will answer about
 * them: an org-scoped plan is refused by `/plans/{id}/run` with a 409 naming the portfolio
 * route, and its `/report` is entitlement-gated. Until the server emitted this field the
 * only way to tell them apart was to match on the title string — which is operator-supplied
 * and defaults to a sentence in English.
 */
export const ENGINE_PLAN_SCOPE_COMPONENT = "component";
export const ENGINE_PLAN_SCOPE_ORG = "org";

/**
 * `""` means the server did not say — an older deployment, not a component plan.
 *
 * Defaulting an absent scope to `component` would label a portfolio pass as a
 * single-component plan, which is precisely the mistake this field was added to end. No
 * claim is made where the server made none.
 */
export type EnginePlanScope =
  | typeof ENGINE_PLAN_SCOPE_COMPONENT
  | typeof ENGINE_PLAN_SCOPE_ORG
  | "";

/**
 * The six coverage buckets. Every contract effect is in exactly one of them.
 *
 * `weakened` is an effect the output PERFORMS but performs with less strength than the
 * source did — the target grants `read` where the source granted `read + edit`. It is not
 * a drop, so it is not `undeclared`; it is not clean, so it is not `carried`. Every family
 * emits it (`automation_contract`, `process_contract` and `contract` all build the list)
 * and it carries the code `CONTRACT_EFFECT_WEAKENED` with a reason naming exactly which
 * grants went missing.
 *
 * `rejected` is its own bucket and not a flavour of the other two: something DID declare
 * a reason, the reason simply is not one that excuses this kind of effect. Folding it
 * into `dropped_with_reason` would report a gate failure as an excused drop, and folding
 * it into `undeclared` would claim nothing said why.
 */
export type EngineCoverageBucket =
  | "carried"
  | "weakened"
  | "dropped_with_reason"
  | "unrepresentable"
  | "rejected"
  | "undeclared";

/**
 * Every bucket the `/effects` endpoint will page, in the engine's own order.
 *
 * THIS TUPLE IS THE PAGING CONTRACT AND IT MUST EQUAL `migrations_api.EFFECT_BUCKETS`.
 * The server's own comment on that tuple states the stakes: it is family-agnostic, and a
 * bucket missing from it is a bucket a paging client never sees. This list is what drives
 * every bucket walk in the UI, so a bucket dropped here is silently invisible rather than
 * broken — which is why `weakened` sat unseen after the server grew it.
 */
export const ENGINE_EFFECT_BUCKETS: readonly EngineCoverageBucket[] = [
  "carried",
  "weakened",
  "dropped_with_reason",
  "unrepresentable",
  "rejected",
  "undeclared",
] as const;

/**
 * Why a plan or task refused to produce anything.
 *
 * A refusal is not a crash and not a partial result — it is the engine declining to
 * emit an artifact it cannot stand behind, and it outranks every other signal on the
 * screen. `outcome` and `code` are both present and usually equal; `code` is the one to
 * render (`_refusal` defaults it to `outcome`).
 */
export interface EngineRefusal {
  code: string;
  outcome?: string;
  message: string;
  objects: string[];
}

/**
 * One effect the parity contract required. `strength` is the set of grants the effect
 * demands (e.g. read/edit); an effect is carried only when the target grants at least
 * as much.
 *
 * `code`, `reason` and `manual_step` are present on every bucket EXCEPT `carried` and
 * `undeclared`. An `undeclared` entry carries a fixed `reason` ("silently dropped — no
 * residue declared") and no code and no manual step, which is exactly why it is a
 * failure: there is nothing to act on.
 */
export interface EngineEffect {
  kind: string;
  target: string;
  strength: string[];
  code?: string;
  reason?: string;
  manual_step?: string;
}

/**
 * One residue item: a source construct the target cannot express, with the manual step
 * that recovers it.
 *
 * THE FIELD NAMES ARE THE ENGINE'S, deliberately. `source_ref` is a reference INTO THE
 * SOURCE ("Account.Rating", "loginIpRanges") — `target` would name it after where it did
 * not land — and `detail` is the engine's own word for the explanation. Residue has no
 * `strength`: there is nothing honest to put in it. This surface used to read both this
 * spelling and the published contract's (`target`/`reason`/`strength`); the wire is
 * authoritative now, so it reads one.
 */
export interface EngineResidueItem {
  code: string;
  source_ref: string;
  detail: string;
  manual_step: string;
}

/**
 * Behaviour that WAS carried and that the target performs differently.
 *
 * Not a coverage bucket and never folded into one: nothing here was lost, so there is
 * nothing for the parity gate to grade and no coverage number can show it. The
 * scheduled-path double fire is the live example — a workflow time trigger is deleted
 * when a record stops meeting the criteria and a Flow scheduled path is not, so a record
 * that qualifies, disqualifies and re-qualifies fires once as a rule and twice as a
 * Flow. It is the single thing a human at the approval gate most needs to be told.
 */
export interface EngineAdvisory {
  code: string;
  severity: string;
  scope: string[];
  detail: string;
  manual_step: string;
}

/** One thing other than this migration that automates the same object. Advisory only. */
export interface EngineBlastEntry {
  kind: string;
  name: string;
  source: string;
  detail: string;
  active: boolean | null;
  stale_risk: boolean;
}

/**
 * What else touches the object this task migrates.
 *
 * `resolved` and `unavailable` are both always present, and that pairing is the whole
 * value of the field: an empty blast radius from three failed arms is a very different
 * claim from an empty one from three successful arms, and only the second means "nothing
 * else touches this object".
 */
export interface EngineBlastRadius {
  advisory: boolean;
  object: string;
  fields: string[];
  entries: EngineBlastEntry[];
  resolved: string[];
  unavailable: string[];
  errors: Record<string, string>[];
  counts: Record<string, number>;
}

/** Per-task check-only validation. `outcome` is lowercase on the wire. */
export interface EngineValidation {
  outcome: EngineValidationOutcome | string;
  message?: string;
  errors?: string[];
  reason?: string | null;
  [key: string]: unknown;
}

/**
 * THE POST-DEPLOY READ-BACK, and the honest limits of it.
 *
 * "Salesforce said Succeeded" and "the org contains what we intended" are different
 * claims, and this is the second one. It is a first-class outcome rather than a note
 * beside one, because the gap between the two is where a silently dropped grant lives.
 *
 * `unread_surfaces` IS THE FIELD THAT KEEPS `identical` HONEST. It lists the children of
 * THIS artifact that the org exposes no readable row for — `recordTypeVisibilities` (no
 * org-side table exists at all) and `applicationVisibilities` (measured live: 21 of 40
 * ids resolved, so a route that used it would report half its members as dropped grants).
 * They are scoped OUT of the diff so a surface nobody could read is never reported as a
 * surface the org dropped — which means a clean diff with a non-empty `unread_surfaces`
 * says "everything we could read matched", not "everything matched". Empty is the only
 * case an `applied` outcome may be read as "the org contains what we intended" without a
 * caveat, and that distinction is invisible unless this list is rendered.
 *
 * `read: false` is the other honest state: the component could not be read back at all,
 * so the apply settled `applied_unverified` — not a failure and not a pass. Something was
 * created and nobody has looked at it.
 */
export interface EngineApplyVerification {
  /** `applied`, `applied_with_mismatch`, `applied_unverified`, … Empty when never run. */
  outcome: string;
  /** `false` when the read-back itself failed. Not the same as a failed comparison. */
  read: boolean;
  /** Why the read failed, when it did. */
  read_error: string | null;
  /**
   * Children of this artifact the org exposes no readable row for, so they were NOT
   * compared. Non-empty means `identical` covers less than the whole document.
   */
  unread_surfaces: string[];
  /** The engine's own sentence. It leads with the unread caveat when there is one. */
  message: string;
  /** Flow only: the component's status in the org. This engine deploys Draft. */
  org_status: string | null;
  /** Flow only: an ACTIVE Flow in the org is running automation. */
  flow_is_active: boolean;
  /** The path-level comparison. Carried through rather than interpreted. */
  diff: Record<string, unknown>;
  /** `true` only when a verification was actually recorded on this attempt. */
  present: boolean;
}

/**
 * One recorded attempt to write to the org. Only real attempts appear — check-only runs
 * are filtered out by the backend, so anything in this list was an attempt to change a
 * customer's org.
 *
 * `kind` distinguishes them and is NOT always `apply`: the decommission phase records its
 * deploys on the same table with `kind` `decommission` / `recommission`, and those name
 * the CUSTOMER'S WorkflowRule in `component_key` rather than anything this engine
 * compiled.
 */
export interface EngineApplyAttempt {
  kind: string;
  /** `open` when the attempt never settled — the state `clear-apply` exists to close. */
  state: string;
  deploy_id: string | null;
  component_key: string;
  metadata_type: string;
  requested_at: string | null;
  submitted_at: string | null;
  /** Set when a later rollback deleted what this attempt created. */
  rolled_back_by: string | null;
  preflight: Record<string, unknown>;
  verification: EngineApplyVerification;
}

/** One component a real apply of this task would CREATE. This phase never modifies. */
export interface EngineWillCreate {
  metadata_type: string;
  component_key: string;
}

/**
 * The write path, per task. Present on EVERY task including ones nobody has ever
 * proposed to apply — an absent key would read as "not checked", and for the one part of
 * this report that is about changing a customer's org that is the worst ambiguity.
 */
export interface EngineApplySummary {
  /** Exactly what a real apply would create, and nothing else. Empty means no artifact. */
  will_create: EngineWillCreate[];
  /** `PLAYGROUND_MIGRATION_APPLY`. When false the write routes are not even mounted. */
  enabled: boolean;
  approved: boolean;
  approval_id: string;
  /** The `compile_key` the recorded approval names. Empty when nobody has approved. */
  approved_compile_key: string;
  /**
   * THE FIELD TO READ AT THE GATE. An approval recorded against an earlier compile is
   * not stale in a way anybody notices by eye — the ledger still says "approved by
   * <name>" — so the comparison is made server-side and stated here.
   */
  approval_matches_artifact: boolean;
  /**
   * The verbatim `APPLY <component> TO <org> <compile_key>` a caller must repeat.
   * Rendered by the backend beside the decision so the string the UI shows and the
   * string the gate checks cannot drift. Empty when there is nothing to apply.
   */
  confirmation_token: string;
  attempts: EngineApplyAttempt[];
  /** A component of this task EXISTS IN THE ORG right now, present tense. */
  wrote_to_org: boolean;
}

// ── the decommission phase ──────────────────────────────────────────────────────

/**
 * WHERE A MIGRATION ACTUALLY IS, as one word.
 *
 * The intermediate phase is named so it cannot be mistaken for an end state by anybody
 * reading a status string: `applied` on its own reads like a finished job, and the whole
 * hazard of this phase is that it is not one.
 */
export const PHASE_NOT_APPLIED = "not_applied";
export const PHASE_REPLACEMENT_LIVE_SOURCE_LIVE = "replacement_live_source_live";
export const PHASE_DECOMMISSION_IN_FLIGHT = "decommission_in_flight";
export const PHASE_SOURCE_DEACTIVATED = "source_deactivated";
export const PHASE_NEEDS_A_HUMAN = "needs_a_human";

export type EngineMigrationPhase =
  | typeof PHASE_NOT_APPLIED
  | typeof PHASE_REPLACEMENT_LIVE_SOURCE_LIVE
  | typeof PHASE_DECOMMISSION_IN_FLIGHT
  | typeof PHASE_SOURCE_DEACTIVATED
  | typeof PHASE_NEEDS_A_HUMAN
  | "";

/** The SOURCE component a decommission would switch off. The customer's own, not ours. */
export interface EngineWillDeactivate {
  metadata_type: string;
  full_name: string;
}

/**
 * THE OTHER HALF OF THE MIGRATION, per task.
 *
 * `apply.wrote_to_org` says a replacement EXISTS. It does not say the migration is done —
 * and the state in which the replacement exists and the customer's original automation is
 * still running is the state this block exists to make visible. If a human has activated
 * the Flow (which this engine never does; it deploys Draft, and activation is a manual act
 * in Setup), BOTH automations are firing: doubled field updates, doubled emails, doubled
 * records. `double_fire_risk` is that fact, and it is deliberately a RISK rather than a
 * claim — the engine cannot know whether the Flow was activated, so it reports what it can
 * stand behind.
 *
 * `applicable` is `false` for every family except `workflow_to_flow`. A Profile is not
 * automation and is never switched off by a migration; a Process Builder process IS
 * automation and DOES have an off switch, but it is a different one (its FlowDefinition's
 * active version is set to 0, not a redeploy of the component) and this engine does not
 * operate it — so a process migration leaves its source running, deliberately and visibly.
 *
 * `confirmation_token` NAMES THE SOURCE. Every other token in this engine names the
 * artifact we compiled; this one reads `DEACTIVATE WorkflowRule Account.Set_Rating IN
 * <org> <compile_key>`, because the deploy behind this gate modifies the customer's own
 * component and a token naming ours would describe the wrong thing to the one person
 * whose job is to check it.
 */
export interface EngineDecommissionSummary {
  /** `false` on any family that has no source automation this phase can switch off. */
  applicable: boolean;
  /** The SOURCE, not the compiled artifact. Empty when `applicable` is false. */
  will_deactivate: EngineWillDeactivate[];
  /** `PLAYGROUND_MIGRATION_DECOMMISSION`. When false the routes are not even mounted. */
  enabled: boolean;
  approved: boolean;
  approval_id: string;
  /** The compile key the recorded GATE_DECOMMISSION approval names. */
  approved_compile_key: string;
  approval_matches_artifact: boolean;
  /** The verbatim `DEACTIVATE WorkflowRule <source> IN <org> <compile_key>`. */
  confirmation_token: string;
  phase: EngineMigrationPhase;
  /**
   * BOTH AUTOMATIONS MAY BE RUNNING RIGHT NOW. The reason this phase exists, and the one
   * thing on a half-done migration that must never be behind a disclosure.
   */
  double_fire_risk: boolean;
  /** `null` when the engine will not assert it either way — mid-flight, or blocked. */
  source_live: boolean | null;
  /** The engine's own sentence about the phase. Rendered verbatim. */
  message: string;
}

/**
 * The result of a decommission or recommission. ALWAYS a 200, including on a refusal —
 * the same rule `/apply` follows, and for the sharper version of the same reason: whether
 * a customer's automation is currently running must never be inferred from a status code.
 *
 * `source` and `component_key` carry the same string: the customer's WorkflowRule.
 */
export interface EngineDecommissionResult {
  plan_id: string;
  task_key: string;
  /** `decommissioned`, `decommission_not_effective`, `decommission_refused`, … */
  outcome: string;
  sent: boolean;
  /** The SOURCE this result is about. */
  source: string;
  component_key: string;
  metadata_type: string;
  gate: string;
  code?: string;
  message?: string;
  /** On CONFIRMATION_MISMATCH: the token the server actually wanted. */
  expected?: string;
  compile_key?: string;
  deploy_id?: string;
}

/**
 * Plan-level counts, returned rather than derived. Every one of these is a sum the
 * client must not compute: `/report` paginates its lists, so a client-side sum is a sum
 * over a page.
 */
export interface EnginePlanCounts {
  tasks: number;
  validated: number;
  unvalidated: number;
  validation_failed: number;
  /** Tasks that produced only residue and declined to compile. Not a failure. */
  abstained: number;
  /** Tasks that refused. A refusal outranks every other signal on a task. */
  refused: number;
  /**
   * Tasks whose SOURCE could not be read in full. A coverage fraction earned against a
   * partially-read source is a weaker claim than the same number earned against a whole
   * one, so this is counted rather than buried.
   */
  source_partial: number;
  /** THE CARDINAL SIN, totalled. Always present, and 0 is the answer we want. */
  undeclared_total: number;
  /** `carried_total`, never `carried` — `carried` is the name of a stripped LIST. */
  carried_total: number;
  total_effects: number;
  /** How many components of this plan exist in the customer's org because of it. */
  applied: number;
}

/**
 * A row from `GET /plans`. This is the WHOLE of `_wire_plan` — the index carries no
 * coverage at all, so there is no `counts` and no `undeclared_total` here and this UI
 * must not print one. The verdict lives in `/report`; see `EnginePlanReport`.
 */
export interface EnginePlanSummary {
  plan_id: string;
  org_id: string;
  family: string;
  title: string;
  /**
   * `component` or `org`, from the server. `""` when it did not say, which is NOT the
   * same as `component` — see `EnginePlanScope`. An org-scoped row is a portfolio pass:
   * it will not run through `/plans/{id}/run` and its report is entitlement-gated.
   */
  scope: EnginePlanScope;
  status: string;
  engine_version: string;
  transform_version: string;
  /** Cancellation is cooperative and durable: requested here, honoured by the executor. */
  cancel_requested: boolean;
  created_at: string;
  updated_at: string;
}

export interface EnginePlanListResponse {
  plans: EnginePlanSummary[];
  limit: number;
  offset: number;
}

export interface EngineTaskReport {
  id: string;
  key: string;
  /** The key, percent-encoded by the SERVER for use in a path segment. Prefer this. */
  key_encoded: string;
  status: string;
  /** The source component this task was planned from, e.g. `{full_name: "Admin"}`. */
  source: Record<string, unknown>;

  // ── where the source came from, and whether we saw all of it ────────────────
  source_route: string;
  /** `false` means the coverage numbers beside it were earned against a partial read. */
  source_complete: boolean;
  /** surface name → [count in org, count in the body we read]. */
  source_counts: Record<string, [number, number]>;
  source_unreadable: string[];
  source_unverified: string[];

  // ── grounding arms ──────────────────────────────────────────────────────────
  menus_resolved: string[];
  /** Arms that did not answer. Coverage above them is inflated by exactly this much. */
  menus_unavailable: string[];
  scope_objects: string[];
  menus_sha: string;
  menus_scope_sha: string;

  // ── coverage ────────────────────────────────────────────────────────────────
  carried_count: number;
  total_effects: number;
  carried_fraction: number;
  /** `carried` is NOT sent here — page it from the effects endpoint. */
  /**
   * Performed, but with less strength than the source demanded. Page it from
   * `/effects?bucket=weakened`.
   *
   * `plan_report` does not currently put this list on the task entry even though
   * `EFFECT_BUCKETS` pages it and every family's `coverage_report` builds it, so this is
   * normally empty and `weakened_count` is normally UNKNOWN. That is read as "the report
   * did not say", never as "none" — see `UNDECLARED_UNKNOWN`.
   */
  weakened: EngineEffect[];
  dropped_with_reason: EngineEffect[];
  unrepresentable: EngineEffect[];
  /** Declared, with a code this kind does NOT admit. A gate failure of its own sort. */
  rejected: EngineEffect[];
  /** Always present, even empty. Non-empty means the engine lost something silently. */
  undeclared: EngineEffect[];

  /**
   * THE AUTHORITATIVE COUNTS. Read these, never `list.length` — the lists page and the
   * counts do not. `UNDECLARED_UNKNOWN` when the server did not send one.
   */
  undeclared_count: number;
  rejected_count: number;
  dropped_count: number;
  unrepresentable_count: number;
  weakened_count: number;
  residue_count: number;

  residue: EngineResidueItem[];

  /** Carried behaviour the target performs differently. Empty on a Profile. */
  advisories: EngineAdvisory[];
  /** The automation family's WHEN. Empty for a Profile, which has no trigger. */
  trigger: Record<string, unknown>;
  trigger_findings: Record<string, unknown>[];
  /** Advisory context for the human at the gate. Never an input to a compiler. */
  blast_radius: EngineBlastRadius | null;

  // ── provenance ──────────────────────────────────────────────────────────────
  contract_sha: string;
  /** A hash over every input that can change a byte. An approval names exactly this. */
  compile_key: string;
  artifact_sha: string;

  /** Lowercased at the wire boundary, and present even when there is no verdict. */
  validation_outcome: string;
  validation: EngineValidation | null;
  refusal: EngineRefusal | null;
  error?: string | null;

  /** The write path, per task. Always present — see `EngineApplySummary`. */
  apply: EngineApplySummary;

  /**
   * THE OTHER HALF OF THE MIGRATION, on the same entry and for the same reason `apply` is:
   * without it a reader sees "applied" and stops, and "applied" is the state in which the
   * customer's OLD automation is still running.
   */
  decommission: EngineDecommissionSummary;
}

export interface EnginePlanReport {
  plan_id: string;
  org_id?: string;
  title?: string;
  status: string;
  family: string;
  created_at?: string;
  updated_at?: string;
  engine_version?: string;
  transform_version?: string;
  menus_sha?: string;
  tasks: EngineTaskReport[];
  counts: EnginePlanCounts;
  /** A component of this plan exists in the org right now. Never inferred. */
  wrote_to_org: boolean;
  applied: number;
  /**
   * Said out loud by the server rather than left for a client to notice: this payload is
   * not the whole report, and `effects_path` is where the rest of it is.
   */
  carried_omitted: boolean;
  effects_path?: string;
  /** Flat sibling of `counts.undeclared_total`, kept by the backend for older readers. */
  undeclared_total: number;
}

export interface EngineEffectsPage {
  plan_id: string;
  task_key: string;
  bucket: EngineCoverageBucket;
  /** The TOTAL beside the page. Never infer "there are more" from a full page. */
  total: number;
  limit: number;
  offset: number;
  items: EngineEffect[];
}

/**
 * One artifact VERSION. Artifacts are append-only: a re-compile with different inputs
 * makes version N+1 and supersedes N rather than overwriting it.
 *
 * There is no `superseded_by` on the wire — the chain is derived from the version list
 * (see `supersededChain`), which is the only source that cannot disagree with itself.
 */
export interface EngineArtifactRecord {
  component_key: string;
  component_key_encoded: string;
  /** Sent, not derived. A client that maintains an extension map maintains a drift. */
  filename: string;
  content_type: string;
  size_bytes: number;
  metadata_type: string;
  version: number;
  content_sha: string;
  /** The key an approval for THIS artifact must name. */
  compile_key: string;
  produced_by: string;
  superseded: boolean;
  created_at: string;
}

export interface EngineArtifactListResponse {
  plan_id: string;
  task_key: string;
  artifacts: EngineArtifactRecord[];
}

/** One artifact's bytes, plus the same metadata the list carries. */
export interface EngineArtifactBody extends EngineArtifactRecord {
  content: string;
}

export type EngineApprovalDecision = "approved" | "rejected";

/**
 * The FOUR gates. Four ledgers, four different sentences, and holding one must never
 * confer another:
 *
 *   GATE_APPLY         this artifact is fit to be written
 *   GATE_ROLLBACK      the component we created may be deleted
 *   GATE_DECOMMISSION  the SOURCE automation this migration replaced may be switched OFF.
 *                      Decided days after the apply, on different evidence — somebody has
 *                      activated the replacement and watched it run.
 *   GATE_RECOMMISSION  the source may be switched back ON.
 */
export type EngineApprovalGate =
  | "GATE_APPLY"
  | "GATE_ROLLBACK"
  | "GATE_DECOMMISSION"
  | "GATE_RECOMMISSION";

/**
 * The three gates whose subject ALREADY EXISTS IN THE ORG, and which the server therefore
 * accepts only an `approved` decision for.
 *
 * Nobody is proposing to touch a component that is already there, so a ledger row saying
 * "rejected" would assert a decision about an act that was never on the table — the server
 * answers 400 to one. `GATE_APPLY` is deliberately absent: its subject does not exist yet,
 * and it is the only gate a REJECTION is a meaningful decision about.
 */
export const ENGINE_APPROVE_ONLY_GATES: readonly EngineApprovalGate[] = [
  "GATE_ROLLBACK",
  "GATE_DECOMMISSION",
  "GATE_RECOMMISSION",
] as const;

export interface EngineApproveRequest {
  task_keys: string[];
  decision: EngineApprovalDecision;
  reason?: string;
  gate?: EngineApprovalGate;
}

export interface EngineApproveResponse {
  plan_id: string;
  gate: string;
  decision: EngineApprovalDecision | string;
  /** The task keys that actually moved. Empty when nothing was awaiting approval. */
  tasks: string[];
  status: string;
}

export interface EngineCancelResponse {
  plan_id: string;
  status: string;
  cancel_requested: boolean;
}

/**
 * The result of a real apply. ALWAYS arrives as a 200, including on a refusal.
 *
 * `sent` is a FIELD, not an inference from a status code — a 403 would say "denied" and
 * leave "was anything sent?" to be worked out from the absence of something else, and
 * whether the org changed is the one thing a caller must never have to infer.
 */
export interface EngineApplyResult {
  plan_id: string;
  task_key: string;
  /** `applied`, `applied_with_mismatch`, `apply_refused`, `apply_indeterminate`, … */
  outcome: string;
  sent: boolean;
  /** On a refusal: the gate factor that fell short. */
  code?: string;
  message?: string;
  /** On CONFIRMATION_MISMATCH: the token the server actually wanted. */
  expected?: string;
  component_key?: string;
  metadata_type?: string;
  compile_key?: string;
  deploy_id?: string;
}

/**
 * THE TWO ROLLBACK ACKNOWLEDGEMENTS, and why there are two.
 *
 * Rollback deletes the component this engine created, and it may do so only when it can
 * prove it created it — the pre-deploy probe said ABSENT and our deploy carries an id.
 * That proof is about PROVENANCE. Whether the component's CONTENT matches what we
 * compiled is a separate question, and it is where these two flags live:
 *
 *   `acknowledge_mismatch`     the apply settled with a diff. "I have seen the diff and
 *                              accept it."
 *   `acknowledge_unverified`   the post-apply read never succeeded. There is no diff to
 *                              have seen, so accepting one would be a sentence that is
 *                              not true. This one says the thing that is: nobody looked.
 *
 * CONFLATING THEM IS THE BUG THE SERVER DELIBERATELY AVOIDED. `NOT_VERIFIED` used to have
 * no escape hatch at all — `acknowledge_mismatch` included — and because the post-apply
 * read named a column `PermissionSet` does not have, EVERY PermissionSet apply settled
 * unverified and then hit that refusal. The engine created a component in a customer's org
 * and would not remove it. Reusing the first flag for the second case would mean the
 * acknowledgement does not name the fact being acknowledged, which is the whole content of
 * an acknowledgement.
 */
export type EngineRollbackAcknowledgement =
  | "acknowledge_mismatch"
  | "acknowledge_unverified"
  | null;

/**
 * The operator exit from a parked apply. Closes an attempt the engine could not settle.
 *
 * IT SENDS NOTHING AND IT DECIDES NOTHING. `sent` is `false` on every response, and
 * `APPLY_ABANDONED` does NOT assert that the org is untouched — the whole reason an
 * attempt reaches this state is that nobody can know. It records that a named person
 * went and looked. The task lands at `blocked`, never `done`.
 */
export interface EngineClearApplyRequest {
  /** The verbatim `ABANDON APPLY OF <component> IN <org> <compile_key>`. */
  confirmation: string;
  /** At least 10 characters, and it goes in the ledger verbatim. */
  reason: string;
}

export interface EngineClearApplyResponse {
  plan_id: string;
  task_key: string;
  outcome: string;
  /** ALWAYS false. Present as a field so nobody infers it from a status code. */
  sent: boolean;
  code?: string;
  message?: string;
  component_key?: string;
  compile_key?: string;
  cleared_by?: string;
  task_status?: string;
  reason?: string;
  cleared_at?: string;
  /** On a CONFIRMATION_MISMATCH refusal: the token the server actually wanted. */
  expected?: string;
}

/** Outcome recorded when a human closed an attempt this engine could not settle. */
export const APPLY_ABANDONED = "APPLY_ABANDONED";
/** A gate factor fell short. NOTHING WAS SENT — the invariant of this outcome. */
export const APPLY_REFUSED = "APPLY_REFUSED";

/** Plan status at which — and only at which — the approval gate is live. */
export const ENGINE_AWAITING_APPROVAL = "awaiting_approval";

/** Task status at which a task is parked mid-apply and `clear-apply` is the only exit. */
export const ENGINE_APPLYING = "applying";

/**
 * THE INTERMEDIATE STATUS. `applied` means the replacement exists in the org AND the
 * source is still live — not that the migration is finished.
 */
export const ENGINE_APPLIED = "applied";
/** The source has been switched off and the org confirmed it. The migration is complete. */
export const ENGINE_DECOMMISSIONED = "decommissioned";
/** A deploy that changes the source rule's active flag is in flight or parked. */
export const ENGINE_DECOMMISSIONING = "decommissioning";
export const ENGINE_RECOMMISSIONING = "recommissioning";
/** Where a mismatched, unverified or ineffective decommission leaves a task. */
export const ENGINE_BLOCKED = "blocked";

/**
 * Sentinel for "the server did not send this count".
 *
 * Absent is NOT zero. Every count is present in a well-formed response, so an absent one
 * means something is wrong with the response — and rendering that as a clean `0
 * undeclared` would manufacture exactly the reassurance the field exists to withhold.
 */
export const UNDECLARED_UNKNOWN = -1;
